import { useMediaQuery } from '@material-ui/core';
import { useTheme } from '@material-ui/core/styles';
import clsx from 'clsx';
import { observer } from 'mobx-react';
import { useStatusDashboardStore } from '../hooks/useStatusDashboardStore';
import { CampaignSortColumn, getColumnFilters } from '../stores/statusDashboardStore';
import { numberWithCommas as formatNumber } from '@roc/feature-utils';
import { DashboardCard } from './dashboardCard';
import { GridFooter } from './gridFooter';
import { ColumnFilter } from './columnFilter';
import { ColumnSelectFilter } from './columnSelectFilter';
import { SortableHeader } from './sortableHeader';
import { MetricBar } from './metricBar';
import { useStatusDashboardStyles } from './statusDashboardStyles';

// First sender of the campaign and how many more; all of them on hover
const CampaignSenders = observer(({ senders }: { senders: string[] }) => {
  const classes = useStatusDashboardStyles();
  const { statusDashboardStore: store } = useStatusDashboardStore();
  if (senders.length === 0) return <span className={classes.muted}>—</span>;
  const names = senders.map(sender => store.senderName(sender));
  return (
    <div title={names.join('\n')} style={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0 }}>
      <span className={classes.ellipsis}>{names[0]}</span>
      {names.length > 1 && <span className={classes.mutedSmall}>+{names.length - 1} more</span>}
    </div>
  );
});

const CampaignHeader = observer(({ column, label }: { column: CampaignSortColumn; label: string }) => {
  const { statusDashboardStore: store } = useStatusDashboardStore();
  return (
    <SortableHeader grid={store.campaignsGrid} column={column} label={label} onSort={() => store.setCampaignSort(column)} />
  );
});

const RecentCampaigns = observer(() => {
  const classes = useStatusDashboardStyles();
  const { statusDashboardStore: store } = useStatusDashboardStore();
  const { channelData } = store;
  const hasEngage = !!channelData.engage;
  const filters = getColumnFilters(store.campaignsGrid);
  const { showSenders } = store;
  const metricColumns = hasEngage ? '.9fr .8fr .9fr .8fr .8fr .8fr' : '.9fr .8fr .9fr .9fr .8fr';
  const columns = `minmax(0,${hasEngage ? 2.2 : 2.4}fr) ${showSenders ? 'minmax(0,1.3fr) ' : ''}${metricColumns}`;

  return (
    <DashboardCard
      title="Recent campaigns"
      subtitle={`${store.roleNote} · ${store.range} · ${formatNumber(store.sentTotal)} messages`}
      right="Select a row to open the campaign"
    >
      <div style={{ display: 'flex', flexDirection: 'column' }}>
        <div className={classes.tableHead} style={{ gridTemplateColumns: columns }}>
          <span>Campaign</span>
          {showSenders && <span>{store.senderLabel}</span>}
          <CampaignHeader column="requested" label="Requested" />
          <CampaignHeader column="sent" label="Sent" />
          <CampaignHeader column="deliveredRate" label="Delivered" />
          <CampaignHeader column="openRate" label={channelData.mid} />
          {hasEngage && <CampaignHeader column="clickRate" label={channelData.engage} />}
          <CampaignHeader column="optOut" label="Opt-outs" />
        </div>
        <div className={classes.filterRow} style={{ gridTemplateColumns: columns }}>
          <ColumnFilter value={filters.name ?? ''} onFilter={value => store.setCampaignFilter('name', value)} />
          {showSenders && (
            <ColumnSelectFilter
              value={filters.sender ?? ''}
              options={store.campaignSenderOptions}
              onFilter={value => store.setCampaignFilter('sender', value)}
              searchable
            />
          )}
        </div>
        {store.campaigns.map(c => (
          <div
            key={c.key}
            className={clsx(classes.tableRow, classes.clickableRow)}
            style={{ gridTemplateColumns: columns }}
            onClick={() => store.openCampaign(c)}
          >
            <div style={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0 }}>
              <span className={classes.bold}>{c.name}</span>
              <span className={classes.mutedSmall}>{c.date} · {c.segment}</span>
            </div>
            {showSenders && <CampaignSenders senders={c.senders} />}
            <div className={classes.right} style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <span className={classes.number}>{formatNumber(c.requested)}</span>
              <span className={classes.mutedSmall}>
                {c.skipped ? `${formatNumber(c.skipped)} skipped` : 'none skipped'}
              </span>
            </div>
            <span className={clsx(classes.right, classes.number)}>{formatNumber(c.sent)}</span>
            <span className={clsx(classes.right, classes.number)}>{c.deliveredRate}</span>
            <span className={clsx(classes.right, classes.number)}>{c.openRate}</span>
            {hasEngage && <span className={clsx(classes.right, classes.number, classes.bold)}>{c.clickRate}</span>}
            <span className={clsx(classes.right, classes.number, classes.muted)} style={{ fontSize: 13 }}>
              {c.optOut ? formatNumber(c.optOut) : '—'}
            </span>
          </div>
        ))}
        <GridFooter
          grid={store.campaignsGrid}
          emptyText={Object.keys(filters).length ? 'No campaigns match the filters' : 'No bulk sends in this range'}
        />
      </div>
    </DashboardCard>
  );
});

const AudienceBreakdown = observer(() => {
  const classes = useStatusDashboardStyles();
  const { statusDashboardStore: store } = useStatusDashboardStore();

  return (
    <DashboardCard title="Audience breakdown" subtitle="Share of sends by audience">
      <div className={classes.stack}>
        {store.audiences.map(a => (
          <div key={a.name} style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 8 }}>
              <span className={classes.bold} style={{ fontSize: 13 }}>{a.name}</span>
              <span className={classes.muted}>{formatNumber(a.size)} · {a.share}%</span>
            </div>
            <MetricBar value={a.share} />
            <div className={classes.mutedSmall} style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
              <span>{a.engagement} engaged</span>
              <span>{a.optOut} opt-out</span>
            </div>
          </div>
        ))}
      </div>
    </DashboardCard>
  );
});

export const OverviewTab = observer(() => {
  const theme = useTheme();
  const isSmall = useMediaQuery(theme.breakpoints.down('md'));
  const { statusDashboardStore: store } = useStatusDashboardStore();
  const columns = store.isMarketing && !isSmall ? 'minmax(0,1.9fr) minmax(0,1fr)' : 'minmax(0,1fr)';

  return (
    <div style={{ display: 'grid', gridTemplateColumns: columns, gap: 16, alignItems: 'start' }}>
      <RecentCampaigns />
      {store.isMarketing && <AudienceBreakdown />}
    </div>
  );
});
